/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { flexRender, type Header, type Table } from '@tanstack/react-table';

import { cssMap, cx, jsx } from '@atlaskit/css';
import DragHandleVerticalIcon from '@atlaskit/icon/core/drag-handle-vertical';
import { Box, Flex, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { TriggerProps } from '@atlaskit/tooltip';

import { type CharlieTableProps, type ColumnActionsConfig } from '../../charlie-table.types';
import { type TableActions } from '../more-actions/more-actions-menu';
import { type ColumnMetadata } from '../more-actions/more-actions.utils';

import { HeaderMoreActions } from './header-more-actions';
import { HeaderSortButton } from './header-sort-button';

const styles = cssMap({
	reorderingStyle: {
		alignItems: 'center',
		width: '100%',
	},
	notReorderingPaddingStyle: {
		paddingInlineStart: token('space.100'),
		alignItems: 'center',
		width: '100%',
	},
	overflowInlineStyle: { overflow: 'hidden', alignItems: 'center', width: '100%' },
	buttonFont: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		flex: 1,
		color: token('color.text.subtlest'),
		textAlign: 'left',
		fontWeight: token('font.weight.bold'),
	},
	buttonFontCurrentSort: {
		color: token('color.text.brand'),
	},
	dragHandlerIconContainerStyle: {
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		width: '1rem',
	},
	hideColumnActions: {
		width: '0px',
		opacity: 0,
		overflow: 'hidden',
	},
	showColumnActions: {
		opacity: 1,
		overflow: 'visible',
		transition: 'opacity 0.2s ',
	},
});

interface HeaderCellContentProps<T extends object> {
	charlieTableColumn: Header<T, unknown>;
	charlieTableState: Table<T>;
	labels: NonNullable<CharlieTableProps<T>['labels']>;
	columnMetadata: ColumnMetadata;
	tableActions: TableActions;
	allowedColumnActionMoves: Set<'left' | 'right'>;
	mergedColumnActions: ColumnActionsConfig;
	canColumnReorderDnd?: boolean;
	canColumnSort?: boolean;
	isMoreActionsEnabled?: boolean;
	showDragHandler?: boolean;
	showSortButton?: boolean;
	sortDirection: false | 'asc' | 'desc';
	showColumnActions?: boolean;
	showMoreActionsButton?: boolean;
	isMoreActionsOpened?: boolean;
	onToggleMoreActions: () => void;
	onMoreActionsClose: () => void;
	onSortButtonFocusChange: (focused: boolean) => void;
	onMoreActionsFocusChange: (focused: boolean) => void;
	interactionName?: string;
	analyticsContext?: Record<string, unknown>;
	tooltipProps: TriggerProps;
}

/**
 * Inner content of a header cell: drag handle, label, sort button, more-actions menu.
 */
export const HeaderCellContent = <T extends object>({
	charlieTableColumn,
	charlieTableState,
	labels,
	columnMetadata,
	tableActions,
	allowedColumnActionMoves,
	mergedColumnActions,
	canColumnReorderDnd,
	canColumnSort,
	isMoreActionsEnabled,
	showDragHandler,
	showSortButton,
	sortDirection,
	showColumnActions,
	showMoreActionsButton,
	isMoreActionsOpened,
	onToggleMoreActions,
	onMoreActionsClose,
	onSortButtonFocusChange,
	onMoreActionsFocusChange,
	interactionName,
	analyticsContext,
	tooltipProps,
}: HeaderCellContentProps<T>) => {
	const moreActionsLabel: ReactNode =
		typeof labels.moreActions === 'function'
			? labels.moreActions(charlieTableColumn)
			: labels.moreActions;

	return (
		<Inline
			spread={canColumnSort || isMoreActionsEnabled ? 'space-between' : undefined}
			xcss={canColumnReorderDnd ? styles.reorderingStyle : styles.notReorderingPaddingStyle}
			{...tooltipProps}
		>
			<Inline xcss={styles.overflowInlineStyle}>
				{canColumnReorderDnd && (
					<Flex xcss={styles.dragHandlerIconContainerStyle}>
						{showDragHandler && (
							<DragHandleVerticalIcon
								label="Drag list item"
								color={token('color.icon')}
								size="small"
							/>
						)}
					</Flex>
				)}
				<Box xcss={cx(styles.buttonFont, sortDirection && styles.buttonFontCurrentSort)}>
					{flexRender(charlieTableColumn.column.columnDef.header, charlieTableColumn.getContext())}
				</Box>
			</Inline>
			{(canColumnSort || isMoreActionsEnabled) && (
				<Inline xcss={showColumnActions ? styles.showColumnActions : styles.hideColumnActions}>
					{canColumnSort && (
						<HeaderSortButton
							charlieTableColumn={charlieTableColumn}
							showSortButton={!!showSortButton}
							sortDirection={sortDirection}
							sortLabel={labels.sortLabel}
							onSortButtonFocusChange={onSortButtonFocusChange}
							onToggleSort={() => charlieTableColumn.column.toggleSorting()}
							interactionName={interactionName}
							analyticsContext={analyticsContext}
						/>
					)}
					{isMoreActionsEnabled && (
						<HeaderMoreActions
							columnMetadata={columnMetadata}
							charlieTableState={charlieTableState}
							tableActions={tableActions}
							labels={labels}
							allowedColumnActionMoves={allowedColumnActionMoves}
							columnActions={mergedColumnActions}
							interactionName={interactionName}
							analyticsContext={analyticsContext}
							showMoreActionsButton={!!showMoreActionsButton}
							isMoreActionsOpened={!!isMoreActionsOpened}
							onToggleMoreActions={onToggleMoreActions}
							onMoreActionsClose={onMoreActionsClose}
							moreActionsLabel={moreActionsLabel}
							onMoreActionsFocusChange={onMoreActionsFocusChange}
							isSortingActive={!!sortDirection}
						/>
					)}
				</Inline>
			)}
		</Inline>
	);
};
